import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EditableText, EditableImage } from '../components/EditableContent';
import { PageSEO } from '../components/SEO';
import PartnersSection from '../components/PartnersSection';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Wand2, PartyPopper, Smile, Calendar, Star, MapPin, Phone, ChevronRight, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagicShowsGrid } from '../components/MagicShowsGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultServices = [
  {
    id: 1,
    title: 'Animation Mascotte',
    description: 'Mickey, Minnie, Stitch, Pat Patrouille, Pikachu, Sonic... Animation mascotte Finistère, Morbihan, Côtes-d\'Armor pour anniversaires et événements.',
    icon: 'Smile',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
    link: '/mascottes',
    color: 'bg-blue-500',
  },
  {
    id: 2,
    title: 'Anniversaire Enfant à Domicile',
    description: 'Clown magicien ou mascotte à domicile à Quimper, Brest, Lorient, Vannes. Animation anniversaire enfant Finistère Morbihan.',
    icon: 'PartyPopper',
    image: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=600&q=80',
    link: '/anniversaire',
    color: 'bg-primary',
  },
  {
    id: 3,
    title: 'Spectacle Magie Enfants',
    description: 'Spectacle clown et magie pour arbre de Noël, camping, CE entreprise en Bretagne. Magicien professionnel Finistère Morbihan.',
    icon: 'Wand2',
    image: 'https://images.pexels.com/photos/17092084/pexels-photo-17092084.jpeg?w=600',
    link: '/spectacles',
    color: 'bg-secondary',
  },
  {
    id: 4,
    title: 'Location Machines Festives',
    description: 'Location barbe à papa, pop-corn, glace italienne, granité à Quimper, Brest, Lorient. Livraison Finistère Morbihan.',
    icon: 'Star',
    image: 'https://images.unsplash.com/photo-1624878021888-af9248dd0a06?w=600&q=80',
    link: '/location',
    color: 'bg-green-500',
  },
  {
    id: 5,
    title: 'Animations Autres',
    description: 'Père Noël pour entreprises (CE, centre commercial, marché Noël), maquillage enfants, tatouages paillettes et bien plus encore ! Découvrez toutes nos animations en Bretagne.',
    icon: 'Calendar',
    image: 'https://customer-assets.emergentagent.com/job_eff36e64-7826-4589-8885-4d6c39b08349/artifacts/qc27kwka_20250610_192920.jpg',
    link: '/animations',
    color: 'bg-purple-500',
  },
];

const defaultTestimonials = [
  {
    id: 7,
    name: 'Chloe Messant',
    location: 'Avis Google',
    text: 'Un immense merci pour cette prestation juste exceptionnelle. J\'ai choisi une mascotte pour l\'anniversaire de ma fille et honnetement tout etait parfait, bienveillance et professionnalisme. C\'etait tout simplement magique. Les enfants etaient emerveilles et les parents aussi. On recommande les yeux fermes ! Je recommande a 1000%.',
    rating: 5,
    date: 'Mai 2026',
    service: 'Animation Mascotte'
  },
  {
    id: 6,
    name: 'Elodie Mousset',
    location: 'Avis Google',
    text: 'Super moment avec Pikachu pour le bapteme de mon p\'tit neveu. Au top !',
    rating: 5,
    date: 'Mai 2026',
    service: 'Animation Mascotte Pikachu'
  },
  {
    id: 1,
    name: 'patou SYL',
    location: 'Avis Google',
    text: 'Très beau spectacle qui a plu autant aux petits que aux grands. Merci clown Guigui',
    rating: 5,
    date: 'Décembre 2025',
    service: 'Spectacle Clown Guigui'
  },
  {
    id: 2,
    name: 'Pauline Brard',
    location: 'Avis Google',
    text: 'Nous avons accueilli Martin le Lutin pour le spectacle de Noël de la commune et nous sommes ravies. Les rires et les étoiles dans les yeux des enfants tout le long du spectacle en témoigne. Merci pour le professionnalisme !',
    rating: 5,
    date: 'Décembre 2025',
    service: 'Spectacle Martin le Lutin'
  },
  {
    id: 3,
    name: 'Cat029 Le Bihan',
    location: 'Avis Google',
    text: 'Un grand merci au clown magicien. Une super prestation entre les maquillages et tatouages paillettes, château gonflable. Mes enfants et leurs ami(e)s étaient au paradis. Le résumé en un mot : EXCEPTIONNEL !',
    rating: 5,
    date: 'Novembre 2025',
    service: 'Anniversaire complet'
  },
  {
    id: 4,
    name: 'Jonathan Eloïse',
    location: 'Avis Google',
    text: 'Un grand merci au clown magicien et à sa mascotte Stitch pour l\'anniversaire de ma fille. Ma fille était aux anges. Merci également pour la patience ainsi que la bienveillance envers les enfants timides.',
    rating: 5,
    date: 'Octobre 2025',
    service: 'Mascotte Stitch'
  },
  {
    id: 5,
    name: 'Suzanna Kazaryan',
    location: 'Avis Google',
    text: 'Nous avons adoré la prestation de la mascotte Chase, super animation ! Le personnel très agréable, je recommande les yeux fermés 100/100. Merci encore.',
    rating: 5,
    date: 'Juillet 2025',
    service: 'Mascotte Chase (Pat Patrouille)'
  },
];

const defaultFaqs = [
  {
    id: 1,
    question: 'Dans quelles villes intervenez-vous ?',
    answer: 'Nous nous déplaçons dans le <strong>Finistère (29)</strong>, le <strong>Morbihan (56)</strong> et les <strong>Côtes-d\'Armor (22)</strong> : Quimper, Brest, Lorient, Vannes, Saint-Brieuc, Lannion, Guingamp, Dinan, Paimpol, Concarneau, Morlaix, Pontivy, Auray, Hennebont, Lamballe, Loudéac, et toutes les communes environnantes.',
  },
  {
    id: 2,
    question: 'Quelles mascottes proposez-vous en location ?',
    answer: 'Nous proposons <strong>Mickey, Minnie, Stitch, Angel, Pikachu, Sonic, Pat Patrouille</strong> (Chase, Marcus), <strong>Super Mario, Luigi, Olaf</strong> et d\'autres personnages sur demande. Toutes nos mascottes sont de qualité professionnelle pour vos anniversaires et événements.',
  },
  {
    id: 3,
    question: 'Combien coûte un anniversaire à domicile ?',
    answer: 'Nos formules <strong>anniversaire à domicile</strong> commencent à partir de <strong>110€</strong>. Le tarif varie selon la durée et les prestations choisies (clown magicien, mascotte). <strong>Contactez-nous pour un devis gratuit personnalisé !</strong>',
  },
  {
    id: 4,
    question: 'Proposez-vous des spectacles pour les arbres de Noël d\'entreprise ?',
    answer: 'Oui ! Nous proposons plusieurs <strong>spectacles pour arbres de Noël</strong> : le spectacle Clown Guigui, Martin le Lutin (spectacle de Noël féérique), et des formules personnalisées pour <strong>CE, entreprises, mairies et associations</strong> en Finistère, Morbihan et Côtes-d\'Armor.',
  },
  {
    id: 5,
    question: 'Peut-on louer des machines à barbe à papa ou pop-corn ?',
    answer: 'Oui, nous proposons la <strong>location de machines festives</strong> : barbe à papa, pop-corn, glace italienne, granité, candy bar. <strong>Livraison et installation comprises</strong> dans tout le Finistère, le Morbihan et les Côtes-d\'Armor pour vos anniversaires, fêtes et événements.',
  },
];

const iconMap = {
  'Smile': Smile,
  'PartyPopper': PartyPopper,
  'Wand2': Wand2,
  'Star': Star,
  'Calendar': Calendar,
};

const reasons = [
  { icon: Calendar, text: 'Plus de 15 ans d\'expérience' },
  { icon: MapPin, text: 'Déplacement Finistère, Morbihan, Côtes-d\'Armor (29, 56, 22)' },
  { icon: Smile, text: 'Spectacles interactifs pour tous âges' },
  { icon: Star, text: 'Animation complète avec gourmandises' },
];

export default function Home() {
  const { isAdmin, getAuthHeaders } = useAuth();
  const [services, setServices] = useState(defaultServices);
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [faqs, setFaqs] = useState(defaultFaqs);
  const [editingService, setEditingService] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isAddingService, setIsAddingService] = useState(false);
  const [isAddingTestimonial, setIsAddingTestimonial] = useState(false);
  const [isAddingFaq, setIsAddingFaq] = useState(false);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content/home/services`);
      if (response.data?.items) setServices(response.data.items);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
    try {
      // 1. Essai prioritaire : source unique de vérité (BDD google_reviews via /api/reviews)
      const reviewsResponse = await axios.get(`${API}/reviews`);
      if (reviewsResponse.data && reviewsResponse.data.length > 0) {
        const mapped = reviewsResponse.data.map((r) => ({
          id: r.id,
          name: r.author,
          location: 'Avis Google',
          text: r.text,
          rating: r.rating,
          date: r.date,
          service: r.prestation || '',
        }));
        setTestimonials(mapped);
      } else {
        // 2. Fallback : ancien système /content/home/testimonials
        const response = await axios.get(`${API}/content/home/testimonials`);
        if (response.data?.items && response.data.items.length > 0 && response.data.items[0].date) {
          setTestimonials(response.data.items);
        }
      }
    } catch (error) {
      console.error('Failed to load testimonials:', error);
    }
    try {
      const response = await axios.get(`${API}/content/home/faqs`);
      if (response.data?.items) setFaqs(response.data.items);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const saveServices = async (newServices) => {
    try {
      await axios.put(
        `${API}/content/home/services`,
        { content: { items: newServices } },
        { headers: getAuthHeaders() }
      );
      setServices(newServices);
      toast.success('Services mis à jour !');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const saveTestimonials = async (newTestimonials) => {
    try {
      await axios.put(
        `${API}/content/home/testimonials`,
        { content: { items: newTestimonials } },
        { headers: getAuthHeaders() }
      );
      setTestimonials(newTestimonials);
      toast.success('Témoignages mis à jour !');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const saveFaqs = async (newFaqs) => {
    try {
      await axios.put(
        `${API}/content/home/faqs`,
        { content: { items: newFaqs } },
        { headers: getAuthHeaders() }
      );
      setFaqs(newFaqs);
      toast.success('FAQ mises à jour !');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleFileUpload = async (e, callback) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() } });
      callback(res.data.image_url);
      toast.success('Image téléchargée !');
    } catch (e) {
      toast.error('Erreur upload');
    }
  };

  const ServiceCard = ({ service, index }) => {
    const IconComponent = iconMap[service.icon] || Star;
    
    return (
      <div className="relative group">
        <Link to={service.link}>
          <Card className="service-card h-full cursor-pointer" data-testid={`service-card-${service.id}`}>
            <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
              <img 
                src={service.image} 
                alt={service.title}
                className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
              />
              <div className={`absolute top-4 left-4 w-12 h-12 ${service.color} rounded-xl flex items-center justify-center shadow-lg`}>
                <IconComponent className="w-6 h-6 text-white" />
              </div>
            </div>
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                {service.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {service.description}
              </p>
            </CardContent>
          </Card>
        </Link>
        {isAdmin && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <button
              onClick={(e) => { e.preventDefault(); setEditingService({ ...service, index }); }}
              className="w-8 h-8 bg-primary text-white rounded-lg flex items-center justify-center shadow-lg"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                if (window.confirm('Supprimer ce service ?')) {
                  const newServices = services.filter((_, i) => i !== index);
                  saveServices(newServices);
                }
              }}
              className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center shadow-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`pt-20 ${isAdmin ? 'pt-28' : 'pt-20'}`} data-testid="home-page">
      <PageSEO page="home" />

      {/* Hero Section */}
      <section className="hero-bg relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute top-32 left-10 animate-float">
          <Star className="w-8 h-8 text-secondary opacity-60" fill="currentColor" />
        </div>
        <div className="absolute top-40 right-20 animate-float" style={{ animationDelay: '1s' }}>
          <Star className="w-6 h-6 text-primary opacity-60" fill="currentColor" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <EditableText 
                  page="home" 
                  section="hero" 
                  field="badge"
                  defaultValue="Animations pour enfants en Bretagne"
                  className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4"
                  as="span"
                />
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  <EditableText 
                    page="home" 
                    section="hero" 
                    field="title1"
                    defaultValue="Magie et Rires"
                    className="gradient-text"
                    as="span"
                  />
                  <br />
                  <EditableText 
                    page="home" 
                    section="hero" 
                    field="title2"
                    defaultValue="pour vos Fêtes !"
                    as="span"
                  />
                </h1>
              </div>
              
              <EditableText 
                page="home" 
                section="hero" 
                field="description"
                defaultValue="Spectacles de magie, mascottes géantes et machines à gourmandises. Créez des souvenirs inoubliables pour vos enfants avec Le Clown Magicien."
                className="text-lg text-muted-foreground max-w-lg"
                multiline
              />

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="btn-bounce rounded-full bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-primary/30"
                    data-testid="hero-cta"
                  >
                    Demander un devis
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:0635512582">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full font-semibold px-8 py-6 text-lg border-2"
                    data-testid="hero-phone"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    06 35 51 25 82
                  </Button>
                </a>
              </div>

              <div className="flex gap-8 pt-4">
                <div>
                  <EditableText page="home" section="stats" field="stat1" defaultValue="15+" className="font-heading text-3xl font-bold text-primary" />
                  <p className="text-sm text-muted-foreground">Années d'expérience</p>
                </div>
                <div>
                  <EditableText page="home" section="stats" field="stat2" defaultValue="1000+" className="font-heading text-3xl font-bold text-secondary" />
                  <p className="text-sm text-muted-foreground">Événements animés</p>
                </div>
                <div>
                  <EditableText page="home" section="stats" field="stat3" defaultValue="4" className="font-heading text-3xl font-bold text-accent" />
                  <p className="text-sm text-muted-foreground">Départements</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <EditableImage 
                  page="home" 
                  section="hero" 
                  field="image"
                  defaultSrc="/images/home/hero-clown-magicien.webp"
                  alt="Le Clown Magicien - Spectacle de magie pour enfants"
                  className="w-full h-[500px] object-contain bg-gradient-to-br from-amber-100 to-amber-50"
                  loading="eager"
                  fetchPriority="high"
                  width={600}
                  height={500}
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-full h-full rounded-3xl bg-gradient-to-br from-primary/20 to-secondary/20 -z-10" />
            </div>
          </div>
        </div>

        <div className="wave-divider">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C360,60 720,0 1080,30 C1260,45 1440,30 1440,30 L1440,60 L0,60 Z" fill="white"/>
          </svg>
        </div>
      </section>

      <MagicShowsGrid testid="home-magic-shows" title="Nos spectacles de magie en Finistère et Morbihan" intro="La magie est notre premier métier. Choisissez le spectacle adapté à votre public : école, arbre de Noël, mairie, camping ou anniversaire." />

      {/* Services Section */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Nos <span className="gradient-text">Prestations</span>
            </h2>
            <EditableText 
              page="home" 
              section="services" 
              field="subtitle"
              defaultValue="Des animations sur mesure pour tous vos événements : anniversaires, arbres de Noël, inaugurations, campings et bien plus encore !"
              className="text-muted-foreground max-w-2xl mx-auto"
              multiline
            />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
            {isAdmin && (
              <button
                onClick={() => setIsAddingService(true)}
                className="h-full min-h-[300px] border-2 border-dashed border-primary/30 rounded-2xl flex flex-col items-center justify-center gap-2 text-primary hover:bg-primary/5 transition-colors"
              >
                <Plus className="w-10 h-10" />
                <span className="font-semibold">Ajouter un service</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
                Pourquoi choisir <span className="gradient-text">Le Clown Magicien</span> ?
              </h2>
              <EditableText 
                page="home" 
                section="why" 
                field="description"
                defaultValue="Depuis plus de 15 ans, nous créons des moments magiques pour les enfants dans toute la Bretagne. Notre passion : voir briller les yeux des petits et des grands !"
                className="text-muted-foreground mb-8"
                multiline
              />
              
              <div className="grid sm:grid-cols-2 gap-4">
                {reasons.map((reason, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                      <reason.icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="font-semibold text-sm">{reason.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <EditableImage 
                page="home" 
                section="why" 
                field="image"
                defaultSrc="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&q=80"
                alt="Événement festif"
                className="rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <p className="font-handwriting text-2xl text-primary">La magie commence ici !</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Avis de <span className="gradient-text">nos clients</span>
            </h2>
            
            {/* Google Reviews Badge */}
            <a 
              href="https://maps.app.goo.gl/L3aU5yCivvDWa4Mt6" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-4 px-6 py-3 bg-white border-2 border-gray-200 rounded-full shadow-lg hover:shadow-xl hover:border-primary/30 transition-all"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-xl text-gray-800">4,9</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-600">50+ avis Google</p>
              </div>
              <span className="text-primary font-semibold text-sm ml-2">Voir tous les avis →</span>
            </a>
          </div>
          
          {/* Reviews Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 5).map((testimonial) => (
              <Card key={testimonial.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
                <CardContent className="p-6">
                  {/* Header with Google icon */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{testimonial.name}</p>
                        {testimonial.date && (
                          <p className="text-xs text-muted-foreground">{testimonial.date}</p>
                        )}
                      </div>
                    </div>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  
                  {/* Stars */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  
                  {/* Review text */}
                  <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                    "{testimonial.text}"
                  </p>
                  
                  {/* Service tag */}
                  {testimonial.service && (
                    <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {testimonial.service}
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* CTA to see more reviews */}
          <div className="text-center mt-8">
            <a 
              href="https://maps.app.goo.gl/L3aU5yCivvDWa4Mt6" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            >
              Voir les 50+ avis sur Google
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <PartnersSection variant="compact" />
      <section className="py-20 bg-gradient-to-r from-primary to-secondary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Prêt à créer un moment magique ?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Contactez-nous dès maintenant pour un devis personnalisé. 
            Nous intervenons dans tout le Finistère, le Morbihan et les Côtes-d'Armor.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="btn-bounce rounded-full bg-white text-primary hover:bg-white/90 font-semibold px-8 py-6 text-lg shadow-xl"
                data-testid="cta-contact"
              >
                Demander un devis gratuit
              </Button>
            </Link>
            <a href="tel:0635512582">
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg"
                data-testid="cta-phone"
              >
                <Phone className="w-5 h-5 mr-2" />
                06 35 51 25 82
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Questions <span className="gradient-text">fréquentes</span>
            </h2>
            <p className="text-muted-foreground">Tout ce que vous devez savoir sur nos services</p>
            {isAdmin && (
              <Button
                onClick={() => setIsAddingFaq(true)}
                className="mt-4"
                data-testid="add-faq-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une FAQ
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={faq.id} className="relative group">
                <details className="group bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-6 cursor-pointer" data-testid={`faq-item-${faq.id}`}>
                  <summary className="font-semibold text-lg flex justify-between items-center list-none pr-8">
                    {faq.question}
                    <span className="text-primary group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground whitespace-pre-line">{faq.answer}</p>
                </details>
                {isAdmin && (
                  <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); setEditingFaq({ ...faq, index }); }}
                      data-testid={`edit-faq-${faq.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => { e.stopPropagation(); saveFaqs(faqs.filter(f => f.id !== faq.id)); }}
                      data-testid={`delete-faq-${faq.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Edit Service Dialog */}
      <Dialog open={!!editingService || isAddingService} onOpenChange={() => { setEditingService(null); setIsAddingService(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingService ? 'Modifier le service' : 'Ajouter un service'}</DialogTitle>
          </DialogHeader>
          <ServiceEditForm 
            service={editingService || { id: Date.now(), title: '', description: '', image: '', link: '/', color: 'bg-primary', icon: 'Star' }}
            onSave={(updatedService) => {
              if (editingService) {
                const newServices = [...services];
                newServices[editingService.index] = updatedService;
                saveServices(newServices);
              } else {
                saveServices([...services, updatedService]);
              }
              setEditingService(null);
              setIsAddingService(false);
            }}
            onCancel={() => { setEditingService(null); setIsAddingService(false); }}
            onUpload={handleFileUpload}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Testimonial Dialog */}
      <Dialog open={!!editingTestimonial || isAddingTestimonial} onOpenChange={() => { setEditingTestimonial(null); setIsAddingTestimonial(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTestimonial ? 'Modifier le témoignage' : 'Ajouter un témoignage'}</DialogTitle>
          </DialogHeader>
          <TestimonialEditForm 
            testimonial={editingTestimonial || { id: Date.now(), name: '', location: '', text: '', rating: 5 }}
            onSave={(updatedTestimonial) => {
              if (editingTestimonial) {
                const newTestimonials = [...testimonials];
                newTestimonials[editingTestimonial.index] = updatedTestimonial;
                saveTestimonials(newTestimonials);
              } else {
                saveTestimonials([...testimonials, updatedTestimonial]);
              }
              setEditingTestimonial(null);
              setIsAddingTestimonial(false);
            }}
            onCancel={() => { setEditingTestimonial(null); setIsAddingTestimonial(false); }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit FAQ Dialog */}
      <Dialog open={!!editingFaq || isAddingFaq} onOpenChange={() => { setEditingFaq(null); setIsAddingFaq(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Modifier la FAQ' : 'Ajouter une FAQ'}</DialogTitle>
          </DialogHeader>
          <FaqEditForm 
            faq={editingFaq || { id: Date.now(), question: '', answer: '' }}
            onSave={(updatedFaq) => {
              if (editingFaq) {
                const newFaqs = [...faqs];
                newFaqs[editingFaq.index] = updatedFaq;
                saveFaqs(newFaqs);
              } else {
                saveFaqs([...faqs, updatedFaq]);
              }
              setEditingFaq(null);
              setIsAddingFaq(false);
            }}
            onCancel={() => { setEditingFaq(null); setIsAddingFaq(false); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Service Edit Form Component
function ServiceEditForm({ service, onSave, onCancel, onUpload }) {
  const [formData, setFormData] = useState(service);

  return (
    <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
      <div>
        <label className="text-sm font-medium">Titre</label>
        <Input 
          value={formData.title} 
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Description</label>
        <Textarea 
          value={formData.description} 
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Image</label>
        <input 
          type="file" 
          accept="image/*" 
          onChange={(e) => onUpload(e, (url) => setFormData({ ...formData, image: url }))} 
          className="w-full mt-1 text-sm" 
        />
        <Input 
          value={formData.image} 
          onChange={(e) => setFormData({ ...formData, image: e.target.value })}
          placeholder="Ou coller l'URL de l'image"
          className="mt-2"
        />
        {formData.image && (
          <img src={formData.image} alt="Aperçu" className="mt-2 h-32 w-full object-cover rounded-lg" />
        )}
      </div>
      <div>
        <label className="text-sm font-medium">Lien (page)</label>
        <Input 
          value={formData.link} 
          onChange={(e) => setFormData({ ...formData, link: e.target.value })}
          className="mt-1"
          placeholder="/mascottes"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(formData)} className="flex-1">Sauvegarder</Button>
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
}

// Testimonial Edit Form Component
function TestimonialEditForm({ testimonial, onSave, onCancel }) {
  const [formData, setFormData] = useState(testimonial);

  return (
    <div className="space-y-4 pt-4">
      <div>
        <label className="text-sm font-medium">Nom</label>
        <Input 
          value={formData.name} 
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Ville</label>
        <Input 
          value={formData.location} 
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Témoignage</label>
        <Textarea 
          value={formData.text} 
          onChange={(e) => setFormData({ ...formData, text: e.target.value })}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Note (1-5)</label>
        <Input 
          type="number"
          min="1"
          max="5"
          value={formData.rating} 
          onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) || 5 })}
          className="mt-1"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(formData)} className="flex-1">Sauvegarder</Button>
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
}

// FAQ Edit Form Component
function FaqEditForm({ faq, onSave, onCancel }) {
  const [formData, setFormData] = useState(faq);

  return (
    <div className="space-y-4 pt-4">
      <div>
        <label className="text-sm font-medium">Question</label>
        <Input 
          value={formData.question} 
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          className="mt-1"
          placeholder="Ex: Dans quelles villes intervenez-vous ?"
          data-testid="faq-question-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Réponse</label>
        <p className="text-xs text-muted-foreground mb-1">
          Vous pouvez utiliser &lt;strong&gt;texte&lt;/strong&gt; pour mettre en gras
        </p>
        <Textarea 
          value={formData.answer} 
          onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
          className="mt-1 min-h-[120px]"
          placeholder="Ex: Nous nous déplaçons dans le <strong>Finistère (29)</strong>..."
          data-testid="faq-answer-input"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(formData)} className="flex-1" data-testid="faq-save-button">Sauvegarder</Button>
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
}
